<template>
  <section
    v-if="fields && cards.length"
    class="c-info-card-row"
    :class="rootClasses"
    :style="rootStyle"
    :id="rendering?.dataSource"
  >
    <ContentGrid :desktop-default-grid="[2, 20]" :mobile-default-grid="[1, 14]">
      <div class="c-info-card-row__header">
        <JssRichText class="c-info-card-row__heading" :field="fields?.title" tag="h2" />
        <RichText
          v-if="fields?.description"
          class="c-info-card-row__intro"
          :field="fields.description"
        />
      </div>

      <div class="c-info-card-row__carousel">
        <div v-if="!isCardStyle && cards.length > 5" class="c-info-card-row__controls">
          <button
            ref="previousButton"
            class="c-info-card-row__control c-info-card-row__control--previous"
            type="button"
            aria-label="Previous cards"
          >
            <span aria-hidden="true">&#8592;</span>
          </button>
          <button
            ref="nextButton"
            class="c-info-card-row__control c-info-card-row__control--next"
            type="button"
            aria-label="Next cards"
          >
            <span aria-hidden="true">&#8594;</span>
          </button>
        </div>
        <div
          v-if="isCardStyle"
          class="c-info-card-row__grid"
          :class="cardGridClasses"
          :style="cardGridStyle"
        >
          <article v-for="card in cards" :key="card.id" class="c-info-card-row__card">
            <h3 v-if="card.fields.title?.value" class="c-info-card-row__title">
              <strong v-html="card.fields.title.value"></strong>
            </h3>

            <RichText
              v-if="card.fields?.description"
              class="c-info-card-row__description"
              :field="card.fields.description"
            />

            <div v-if="card.fields.image" class="c-info-card-row__media">
              <AdaptiveImage
                class="c-info-card-row__image"
                :field="card.fields.image"
                :show="true"
                :is-background="false"
                :enable-scroll-detection="false"
                :show-image-redius="false"
              />
            </div>

            <a
              v-if="card.href"
              class="c-info-card-row__link"
              :href="card.href"
              :target="card.target"
              :rel="card.target === '_blank' ? 'noopener noreferrer' : undefined"
              :aria-label="card.linkLabel"
            >
              <span aria-hidden="true"></span>
            </a>
          </article>
        </div>

        <Swiper
          v-else
          class="c-info-card-row__swiper"
          :modules="swiperModules"
          :slides-per-view="1.12"
          :space-between="16"
          :pagination="paginationOptions"
          :navigation="navigationOptions"
          :breakpoints="swiperBreakpoints"
          @swiper="onSwiper"
        >
          <SwiperSlide v-for="card in cards" :key="card.id" class="c-info-card-row__slide">
            <article
              class="c-info-card-row__card"
              @mouseenter="onCardEnter"
              @mouseleave="onCardLeave"
            >
              <span class="c-info-card-row__mark" aria-hidden="true"></span>

              <h3 v-if="card.fields.title?.value" class="c-info-card-row__title">
                <strong v-html="card.fields.title.value"></strong>
              </h3>

              <RichText
                v-if="card.fields?.description"
                class="c-info-card-row__description"
                :field="card.fields.description"
              />

              <div v-if="card.fields.image" class="c-info-card-row__media">
                <AdaptiveImage
                  class="c-info-card-row__image"
                  :field="card.fields.image"
                  :show="true"
                  :is-background="false"
                  :enable-scroll-detection="false"
                  :show-image-redius="false"
                />
              </div>

              <a
                v-if="card.href"
                class="c-info-card-row__link"
                :href="card.href"
                :target="card.target"
                :rel="card.target === '_blank' ? 'noopener noreferrer' : undefined"
                :aria-label="card.linkLabel"
              >
                <span aria-hidden="true"></span>
              </a>
            </article>
          </SwiperSlide>
        </Swiper>
      </div>
    </ContentGrid>
  </section>
</template>

<script setup lang="ts">
import { computed, nextTick, ref } from 'vue';
import { Navigation, Pagination } from 'swiper/modules';
import { Swiper, SwiperSlide } from 'swiper/vue';
import { RichText, Text as JssText } from '@sitecore-jss/sitecore-jss-vue';
import 'swiper/css';
import 'swiper/css/pagination';
import type { InfoCardRowCardFields, InfoCardRowProps } from '@/types/components/InfoCardRow';
import type { ScItem } from '@/types/sitecore';
import utils from '@/utils';

interface NormalizedCard extends ScItem<InfoCardRowCardFields> {
  fields: InfoCardRowCardFields;
  href: string;
  target: string | undefined;
  linkLabel: string;
}

const props = defineProps<InfoCardRowProps>();
const previousButton = ref<HTMLElement | null>(null);
const nextButton = ref<HTMLElement | null>(null);
const fields = computed(() => props.fields);
const swiperModules = [Pagination, Navigation];
const isCardStyle = computed(() => Boolean(fields.value?.isCardStyle?.value));
const rootClasses = computed(() => {
  const baseClasses = utils.overall.baseComClass(props.fields, 'c-info-card-row');
  return [
    baseClasses,
    {
      'c-info-card-row--card-style': isCardStyle.value,
      'c-info-card-row--without-header': !hasHeader.value,
      'c-info-card-row--hide-pagination-mobile': cards.value.length <= 1,
      'c-info-card-row--hide-pagination-tablet': cards.value.length <= 2,
      'c-info-card-row--hide-pagination-desktop': cards.value.length <= 5,
    },
  ];
});
const hasHeader = computed(
  () => Boolean(fields.value?.title?.value) || Boolean(fields.value?.description?.value)
);

const paginationOptions = {
  clickable: true,
  bulletClass: 'icr-swiper-dot',
  bulletActiveClass: 'is-active',
};

const navigationOptions = computed(() =>
  cards.value.length > 5
    ? {
        prevEl: previousButton.value,
        nextEl: nextButton.value,
      }
    : false
);

const onSwiper = async (instance: any) => {
  await nextTick();
  if (!previousButton.value || !nextButton.value || !instance.navigation) return;

  instance.params.navigation = {
    ...instance.params.navigation,
    prevEl: previousButton.value,
    nextEl: nextButton.value,
  };
  instance.navigation.destroy();
  instance.navigation.init();
  instance.navigation.update();
};

// hover 时先锁定卡片为未 hover 的高度，再加 is-hovered 类应用 hover 样式，
// 确保卡片高度不随文案展开而变化，也不会触发 swiper stretch 去拉高其余卡片
const onCardEnter = (event: MouseEvent) => {
  const card = event.currentTarget as HTMLElement;
  card.style.height = `${card.offsetHeight}px`;
  card.classList.add('is-hovered');
};

const onCardLeave = (event: MouseEvent) => {
  const card = event.currentTarget as HTMLElement;
  card.classList.remove('is-hovered');
  card.style.height = '';
};

const swiperBreakpoints = computed(() => ({
  768: {
    slidesPerView: Math.min(cards.value.length, 2),
    spaceBetween: 24,
  },
  880: {
    slidesPerView: Math.min(cards.value.length, 3),
    spaceBetween: 16,
  },
  1200: {
    slidesPerView: Math.min(cards.value.length, 5),
    spaceBetween: 16,
  },
}));

const rootStyle = computed(() => utils.overall?.baseComStyle?.(fields.value) || {});

const rawCards = computed<ScItem<InfoCardRowCardFields>[]>(() => {
  const cardItems = utils.sc.getFieldObj(fields.value, 'cards');
  return Array.isArray(cardItems) ? cardItems : [];
});

const cards = computed<NormalizedCard[]>(() =>
  rawCards.value.reduce<NormalizedCard[]>((result, item, index) => {
    const itemFields = item.fields;
    if (!itemFields) return result;

    const linkValue = itemFields.link?.value || {};
    const title = itemFields.title?.value || `Info card ${index + 1}`;

    result.push({
      ...item,
      fields: itemFields,
      href: linkValue.href || '',
      target: linkValue.target,
      linkLabel: linkValue.text || linkValue.title || title,
    });

    return result;
  }, [])
);

const cardGridStyle = computed(() => {
  const count = cards.value.length;
  const createRowVariables = (prefix: string, maxColumns: number) => {
    const columns = Math.min(count, maxColumns);
    return {
      [`--${prefix}-columns`]: `${columns}`,
      [`--${prefix}-gaps`]: `${Math.max(columns - 1, 0) * 16}px`,
    };
  };

  return {
    ...createRowVariables('icr-tablet', 2),
    ...createRowVariables('icr-landscape', 3),
    ...createRowVariables('icr-desktop', 5),
  };
});

const cardGridClasses = computed(() => ({
  'c-info-card-row__grid--multi-row-mobile': cards.value.length > 1,
  'c-info-card-row__grid--multi-row-tablet': cards.value.length > 2,
  'c-info-card-row__grid--multi-row-landscape': cards.value.length > 3,
  'c-info-card-row__grid--multi-row-desktop': cards.value.length > 5,
}));
</script>

<style lang="scss">
@import '../../styles/variables';
@import '../../styles/functions';
@import '../../styles/mixins';

.c-info-card-row {
  overflow: hidden;
  padding: 40px 0;

  &__carousel {
    position: relative;
    max-width: 1440px;
    margin: 0 auto;
  }

  &__controls {
    position: absolute;
    top: -104px;
    right: 0;
    z-index: 3;
    display: flex;
    gap: 12px;
  }

  &--without-header {
    .c-info-card-row__carousel {
      padding-top: 80px;
    }

    .c-info-card-row__controls {
      top: 0;
    }
  }

  &__control {
    width: 56px;
    height: 56px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    border: 1px solid #969696;
    border-radius: 50%;
    background: $white;
    color: #222;
    font-size: 26px;
    line-height: 1;
    cursor: pointer;
    transition:
      background-color 180ms ease,
      color 180ms ease,
      border-color 180ms ease;

    &:hover,
    &:focus-visible {
      border-color: #739b78;
      background: #739b78;
      color: $white;
      outline: none;
    }

    &.swiper-button-disabled {
      opacity: 0.45;
      cursor: not-allowed;
    }
  }

  &__header {
    max-width: 1440px;
    margin: 0 auto 40px;
  }

  &__heading {
    text-align: center;
  }

  &__intro {
    max-width: 1160px;
    margin-top: 18px;
    color: #404040;
    font-size: 16px;
    line-height: 1.5;

    p {
      margin: 0;
    }
  }

  &__swiper {
    overflow: visible;
    padding-bottom: 36px;

    .swiper-wrapper {
      align-items: stretch;
    }
  }

  &__grid {
    display: flex;
    flex-wrap: wrap;
    justify-content: center;
    gap: 16px;

    > .c-info-card-row__card {
      flex: 0 0 100%;
    }

    &--multi-row-mobile {
      justify-content: flex-start;
    }
  }

  &__slide {
    display: flex;
    height: auto;

    .c-info-card-row__card {
      flex: 1 0 auto;
      min-height: 100%;
    }
  }

  &__card {
    position: relative;
    display: flex;
    flex-direction: column;
    min-height: 0;
    width: 100%;
    overflow: hidden;
    padding: 20px;
    border: 1px solid #d9ded8;
    border-radius: 8px;
    color: #404040;
    background: $white;
    box-shadow: 0 14px 32px rgba(26, 43, 34, 0.04);
    transition:
      background-color 180ms ease,
      border-color 180ms ease,
      color 180ms ease,
      box-shadow 180ms ease;
  }

  &__mark {
    display: block;
    width: 24px;
    height: 10px;
    margin-bottom: 10px;
    transform: skewX(-42deg);
    background: $color-growth-green;
    border-radius: 0 2px 0 2px;
  }

  &__title {
    margin: 0;
  }

  &__description {
    margin-top: 16px;
    margin-bottom: 16px;
    color: #566174;
    font-size: 19px;
    line-height: 1.45;
    font-weight: 400;

    p {
      margin: 0;
    }

    p + p {
      margin-top: 14px;
    }
  }

  &__media {
    margin-top: auto;
    flex: 0 0 auto;
    overflow: hidden;
    border-radius: 8px;
  }

  &__image {
    display: block;
    width: 100%;
    height: auto;

    .e-background-image {
      height: auto;
    }

    > div,
    .e-background-image,
    .e-background-image__img,
    img {
      display: block;
      width: 100%;
      height: auto;
    }
  }

  &__link {
    position: absolute;
    inset: 0;
    display: block;
    width: 100%;
    height: 100%;
    z-index: 1;
    cursor: pointer;
    color: $white;
    text-decoration: none;

    &:focus-visible {
      outline: 2px solid #739b78;
      outline-offset: -6px;
    }

    span {
      position: absolute;
      left: 36px;
      bottom: 42px;
      width: 23px;
      height: 23px;
      opacity: 0;
      transform: translate(4px, 4px);
      pointer-events: none;
      transition:
        opacity 180ms ease,
        transform 180ms ease;

      &::before,
      &::after {
        content: '';
        position: absolute;
        box-sizing: border-box;
      }

      &::before {
        left: 2px;
        top: 10px;
        width: 18px;
        height: 3px;
        background: currentColor;
        transform: rotate(-45deg);
        transform-origin: center;
      }

      &::after {
        right: 3px;
        top: 2px;
        width: 12px;
        height: 12px;
        border-top: 3px solid currentColor;
        border-right: 3px solid currentColor;
      }
    }
  }

  .swiper-pagination {
    bottom: 0;
    display: flex;
    justify-content: center;
    gap: 10px;
  }

  .icr-swiper-dot {
    display: block;
    width: 30px;
    height: 5px;
    margin: 0;
    transform: skewX(-34deg);
    background: #eceeed;
    border-radius: 0;
    opacity: 1;
    cursor: pointer;
  }

  .icr-swiper-dot.is-active {
    width: 44px;
    background: #739b78;
  }

  &--hide-pagination-mobile {
    .swiper-pagination {
      display: none !important;
    }

    .c-info-card-row__swiper {
      padding-bottom: 0;
    }
  }

  @media (hover: hover) and (pointer: fine) {
    &:not(.c-info-card-row--card-style) &__card.is-hovered,
    &:not(.c-info-card-row--card-style) &__card:focus-within {
      color: $white;
      background: #739b78;
      border-color: #739b78;
      box-shadow: 0 22px 42px rgba(58, 94, 66, 0.2);

      .c-info-card-row__mark {
        background: $white;
      }

      .c-info-card-row__description {
        color: $white;
      }

      .c-info-card-row__media {
        display: none;
      }

      .c-info-card-row__link {
        &:focus-visible {
          outline-color: $white;
        }

        span {
          opacity: 1;
          transform: translate(0, 0);
        }
      }
    }
  }

  @include tablet {
    &--hide-pagination-mobile {
      .swiper-pagination {
        display: flex;
      }

      .c-info-card-row__swiper {
        padding-bottom: 36px;
      }
    }

    &--hide-pagination-tablet {
      .swiper-pagination {
        display: none !important;
      }

      .c-info-card-row__swiper {
        padding-bottom: 0;
      }
    }

    &__swiper {
      padding-bottom: 36px;
    }

    &__controls {
      top: -96px;
    }

    &__grid {
      > .c-info-card-row__card {
        flex-basis: calc((100% - var(--icr-tablet-gaps)) / var(--icr-tablet-columns));
      }

      &--multi-row-tablet {
        justify-content: flex-start;
      }
    }

    &__card {
      padding: 24px;
    }

    &__media {
      margin-top: auto;
    }

    &__description {
      font-size: 18px;
    }
  }

  @include tablet-landscape {
    &__grid {
      > .c-info-card-row__card {
        flex-basis: calc((100% - var(--icr-landscape-gaps)) / var(--icr-landscape-columns));
      }

      &--multi-row-landscape {
        justify-content: flex-start;
      }
    }

    &__card {
      padding: 24px;
    }

    &__mark {
      margin-bottom: 10px;
    }

    &__description {
      margin-top: 16px;
      font-size: 18px;
      line-height: 1.45;
    }

    &__media {
      margin-top: auto;
    }

    &__link {
      span {
        left: 30px;
        bottom: 32px;
      }
    }
  }

  @media (min-width: 1200px) {
    &__grid {
      > .c-info-card-row__card {
        flex-basis: calc((100% - var(--icr-desktop-gaps)) / var(--icr-desktop-columns));
      }

      &--multi-row-desktop {
        justify-content: flex-start;
      }
    }

    &__swiper {
      overflow: hidden;
    }

    &--hide-pagination-desktop {
      .swiper-pagination {
        display: none !important;
      }

      .c-info-card-row__swiper {
        padding-bottom: 0;
      }
    }
  }
}
</style>
